/* ============================================================================
   ACEPLORE · module-search.js — predictive search overlay (<lumina-search>).
   Uses Shopify Predictive Search API + Section Rendering fallback.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, config } = window.Lumina;

  class LuminaSearch extends window.Lumina.Element {
    connectedCallback() {
      this.overlay = this.querySelector('[data-search-overlay]');
      this.panel = this.querySelector('.lm-search__panel');
      this.form = this.querySelector('[data-search-form]');
      this.input = this.querySelector('[data-search-input]');
      this.live = this.querySelector('[data-search-live]');
      this.defaultView = this.querySelector('[data-search-default]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.cache = new Map();
      this.release = null;

      this.listen(document, 'click', (e) => {
        if (e.target.closest('[data-search-open]')) { e.preventDefault(); this.open(); }
      });
      this.querySelectorAll('[data-search-close]').forEach((b) => this.listen(b, 'click', () => this.close()));
      this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
        if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.open(); }
      });
      this.listen(this.input, 'input', utils.debounce(() => this.onInput(), 220));
      this.listen(this.clearBtn, 'click', () => { this.input.value = ''; this.input.focus(); this.onInput(); });
      this.querySelectorAll('[data-search-term]').forEach((chip) =>
        this.listen(chip, 'click', () => { this.input.value = chip.textContent.trim(); this.input.focus(); this.onInput(); })
      );
    }

    open() {
      if (this.isOpen) { this.input.focus(); return; }
      this.isOpen = true;
      this.panel.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      this.overlay.setAttribute('aria-hidden', 'false');
      this.syncTriggers();
      utils.lockScroll(true);
      this.release = utils.trapFocus(this.panel, document.activeElement);
      setTimeout(() => this.input.focus(), 50);
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.release && this.release();
      this.release = null;
      this.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('inert', '');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.syncTriggers();
      utils.lockScroll(false);
    }
    syncTriggers() {
      document.querySelectorAll('[data-search-open][aria-expanded]').forEach((b) => b.setAttribute('aria-expanded', String(this.isOpen)));
    }

    onInput() {
      const q = this.input.value.trim();
      this.clearBtn.hidden = q.length === 0;
      if (q.length < 2) { this.showDefault(); return; }
      this.search(q);
    }
    showDefault() { this.defaultView.hidden = false; this.live.hidden = true; }

    async search(q) {
      this.defaultView.hidden = true;
      this.live.hidden = false;
      if (this.cache.has(q)) { this.live.innerHTML = this.cache.get(q); this.bindResults(); return; }
      this.live.innerHTML = this.skeleton();
      try {
        // Merchant "Search & discovery" toggles decide which resource types we
        // request and render.
        const f = (config.features && config.features.search) || {};
        const types = [];
        if (f.products !== false) types.push('product');
        if (f.collections !== false) types.push('collection');
        if (f.pages !== false) types.push('page');
        if (f.articles !== false) types.push('article');
        if (f.queries !== false) types.push('query');
        const params = new URLSearchParams({
          q,
          'resources[type]': types.join(',') || 'product',
          'resources[limit]': '8',
          'resources[options][unavailable_products]': 'last',
          'resources[options][fields]': 'title,product_type,variants.title,vendor,body',
        });
        // The JSON flavor of predictive search lives at <route>.json; adding a
        // section_id would switch the endpoint to Section Rendering (HTML) mode.
        const data = await utils.fetchJSON(config.routes.predictiveSearch + '.json?' + params.toString());
        // Stale guard: a slow earlier query must not overwrite newer results.
        if (this.input.value.trim() !== q) return;
        const results = (data.resources && data.resources.results) || {};
        const html = this.render(results, q);
        this.cache.set(q, html);
        this.live.innerHTML = html;
        this.bindResults();
      } catch (err) {
        if (this.input.value.trim() !== q) return;
        this.live.innerHTML = `<p class="lm-search__empty">${utils.escapeHTML(utils.t('searchError', 'Search is unavailable right now. Please try again.'))}</p>`;
      }
    }

    // Fill a locale template like 'No results found for "{{ terms }}".'
    tpl(key, fallback, q) {
      const raw = utils.t(key, fallback);
      const safe = `<strong>${utils.escapeHTML(q)}</strong>`;
      return raw.indexOf('{{') !== -1 ? raw.replace(/\{\{\s*terms\s*\}\}/g, safe) : `${raw} ${safe}`;
    }

    render(r, q) {
      if (!r) return '';
      const hasResults = ['products', 'collections', 'pages', 'articles', 'queries'].some((k) => r[k] && r[k].length);
      if (!hasResults) {
        return `<div class="lm-search__empty"><p>${this.tpl('noResults', 'No results found for "{{ terms }}".', q)}</p></div>`;
      }
      let html = '';
      if (r.queries && r.queries.length) {
        html += `<div class="lm-search__queries"><h3 class="lm-search__heading">${utils.escapeHTML(utils.t('searchSuggestions', 'Suggestions'))}</h3>`;
        html += r.queries.slice(0, 5).map((item) => `<a href="${utils.escapeHTML(item.url)}" class="lm-search__query-chip">${utils.escapeHTML(item.text)}</a>`).join('');
        html += '</div>';
      }
      html += '<div class="lm-search__grid">';
      if (r.products && r.products.length) {
        html += `<div class="lm-search__section lm-search__section--products"><h3 class="lm-search__heading">${utils.escapeHTML(utils.t('searchProducts', 'Products'))}</h3><div class="lm-search__products">`;
        html += r.products.map((p) => this.productCard(p)).join('');
        html += `</div></div>`;
      }
      const lists = [
        ['collections', utils.t('searchCollections', 'Collections')],
        ['pages', utils.t('searchPages', 'Pages')],
        ['articles', utils.t('searchArticles', 'Articles')],
      ];
      const sideCols = lists.filter(([k]) => r[k] && r[k].length);
      if (sideCols.length) {
        html += '<div class="lm-search__side">';
        sideCols.forEach(([k, label]) => {
          html += `<div class="lm-search__section"><h3 class="lm-search__heading">${utils.escapeHTML(label)}</h3><ul class="lm-search__list" role="list">`;
          html += r[k].map((item) => `<li><a href="${utils.escapeHTML(item.url)}" class="lm-search__list-link">${utils.escapeHTML(item.title)}</a></li>`).join('');
          html += `</ul></div>`;
        });
        html += '</div>';
      }
      html += '</div>';
      const url = config.routes.search + '?type=product&q=' + encodeURIComponent(q);
      html += `<a href="${url}" class="lm-btn lm-btn--secondary lm-btn--full lm-search__viewall">${this.tpl('viewAll', 'View matching products for "{{ terms }}"', q)} →</a>`;
      return html;
    }

    productCard(p) {
      let imageUrl = '';
      if (p.featured_image && p.featured_image.url) {
        try {
          const u = new URL(p.featured_image.url, window.location.origin);
          u.searchParams.set('width', '120');
          imageUrl = u.toString();
        } catch (_) { imageUrl = p.featured_image.url; }
      }
      const img = imageUrl
        ? `<img src="${utils.escapeHTML(imageUrl)}" alt="" width="60" height="60" loading="lazy">`
        : '<div class="lm-placeholder"></div>';
      const price = p.price != null && p.price !== '' ? utils.formatMoney(Math.round(Number(p.price) * 100)) : '';
      return `<a href="${utils.escapeHTML(p.url)}" class="lm-search__product">
        <span class="lm-search__product-media">${img}</span>
        <span class="lm-search__product-info">
          <span class="lm-search__product-title">${utils.escapeHTML(p.title)}</span>
          <span class="lm-search__product-price">${price}</span>
        </span>
      </a>`;
    }

    skeleton() {
      let s = '<div class="lm-search__products">';
      for (let i = 0; i < 4; i++) s += '<div class="lm-search__product"><span class="lm-skeleton" style="width:60px;height:60px;border-radius:8px"></span><span style="flex:1"><span class="lm-skeleton" style="display:block;height:12px;margin-bottom:8px"></span><span class="lm-skeleton" style="display:block;height:12px;width:40%"></span></span></div>';
      return s + '</div>';
    }
    bindResults() {
      // Hook for wishlist/quick-add inside results if added later.
    }
  }
  if (!customElements.get('lumina-search')) customElements.define('lumina-search', LuminaSearch);
})();
